import { Component, OnInit } from '@angular/core';
import { EntryPass, EntryPassDiscount} from '../../../../../../../models/entry-pass';
import { MyAccount } from '../../../../../../../models/my-account';
import { ActivatedRoute, Router } from '@angular/router';
import { EntryPassService } from '../../../../../../../services/entry-pass/entry-pass.service';
import { AccountService } from '../../../../../../../services/account/account.service';
import { OrganizationConfigService } from '../../../../../../../services/organization-config/organization-config.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SimpleMessageModalComponent } from '../../../../../../../components/modals/simple-message-modal/simple-message-modal.component';
import { Association } from '../../../../../../../models/association';
import { AssociationService } from '../../../../../../../services/association/association.service';
import { Membership } from '../../../../../../../models/membership';
import { MembershipService } from '../../../../../../../services/membership/membership.service';
import { EntryPassDiscountType, EntryPassStatus } from '../../../../../../../models/entry-pass-enums';
import { MembershipStatus } from '../../../../../../../models/membership-enums';


@Component({
  selector: 'app-entry-pass-discount-detail',
  templateUrl: './entry-pass-discount-detail.component.html',
  styleUrls: ['./entry-pass-discount-detail.component.css']
})
export class EntryPassDiscountDetailComponent implements OnInit {
  public isNewDiscount: boolean;
  public discount: EntryPassDiscount = {
    id: null,
    url: null,
    is_automatic: false,
    code: '',
    name: '',
    discount_type: EntryPassDiscountType.currency,
    amount: null,
    start_date: null,
    end_date: null,
    entry_pass_ids: [],
    entry_pass_names: [],
    requires_association: false,
    requires_all_associations: false,
    association_ids: [],
    association_names: [],
    requires_membership: false,
    required_membership_ids: [],
    required_membership_names: [],
    max_number_of_uses_per_customer: null,
    required_no_check_in_period_days: null,
    min_check_in_count: null,
    max_check_in_count: null,
    min_number_of_days_without_membership: null,
    min_age: null,
    max_age: null,
    created_at: null,
    updated_at: null,
    last_updated_by_id: null,
    last_updated_by_first_name: null,
    last_updated_by_last_name: null,
    last_updated_by_email: null,
  };
  public entryPasses: EntryPass[];
  public associations: Association[];
  public memberships: Membership[];

  // Used to ensure retired entry passes are included as options if they are already on the discount.
  // Otherwise, retired entry passes are not shown.
  public initialEntryPassIds: number[] = [];

  public errorMessage = '';
  public errors: any = {};

  public loadingExistingDiscount = false;
  public loadingEntryPasses = true;
  public loadingAssociations = true;
  public loadingMemberships = true;
  public loadingSubmit = false;
  public loadingDelete = false;

  public discountNotFound = false;
  public serverError: number = null;

  public myAccount: MyAccount;

  public EntryPassStatus = EntryPassStatus;
  public EntryPassDiscountType = EntryPassDiscountType;
  public MembershipStatus = MembershipStatus;

  constructor(
    public orgConfig: OrganizationConfigService,
    private route: ActivatedRoute,
    private router: Router,
    private entryPassService: EntryPassService,
    private associationService: AssociationService,
    private membershipService: MembershipService,
    private accountService: AccountService,
    private modalService: NgbModal,
  ) {}

  ngOnInit() {
    const routeParams = this.route.snapshot.params;
    this.isNewDiscount = !routeParams.hasOwnProperty('id');

    this.myAccount = this.accountService.getMyAccount();
    if (!this.myAccount.is_organization_admin) { return; }

    // Get the discount to edit, if an ID was provided.
    if (!this.isNewDiscount) {
      const idInt = parseInt(routeParams.id, 10);
      if (isNaN(idInt)) {
        this.discountNotFound = true;
        this.loadingExistingDiscount = false;
        return;
      }

      this.loadDiscount(idInt);
    }

    this.loadEntryPasses();
    this.loadAssociations();
    this.loadMemberships();
  }

  public loadDiscount(id: number) {
    this.loadingExistingDiscount = true;

    this.entryPassService
      .getEntryPassDiscount(id)
      .then(discount => {
        this.discount = discount;
        this.initialEntryPassIds = [...this.discount.entry_pass_ids];
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.discountNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingExistingDiscount = false; });
  }

  public loadEntryPasses() {
    this.loadingEntryPasses = true;

    this.entryPassService
      .getEntryPasses(1, 3000, {})
      .then(result => {
        this.entryPasses = result.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.discountNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEntryPasses = false; });
  }

  public loadAssociations() {
    this.loadingAssociations = true;

    this.associationService
      .getAssociations(1, 3000, {})
      .then(result => {
        this.associations = result.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.discountNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingAssociations = false; });
  }

  public loadMemberships() {
    this.loadingMemberships = true;

    this.membershipService
      .getMemberships(
        1,
        3000,
        {include_rolling: true}
      )
      .then(result => {
        this.memberships = result.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.discountNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingMemberships = false; });
  }

  public handleSubmit() {
    this.loadingSubmit = true;

    this.errorMessage = '';
    this.errors = {};

    let savePromise: Promise<EntryPassDiscount>;
    if (this.isNewDiscount) {
      savePromise = this.entryPassService.addEntryPassDiscount(this.discount);
    } else {
      savePromise = this.entryPassService.updateEntryPassDiscount(this.discount);
    }

    savePromise
      .then(savedDiscount => {
        this.discount = savedDiscount;

        let successMessage: string;
        let discountName = this.discount.name;
        if (!discountName) {
          discountName = this.discount.code;
        }
        if (this.isNewDiscount) {
          successMessage = discountName + ' created successfully.';
        } else {
          successMessage = discountName + ' updated successfully.';
        }

        this.router.navigate(
          ['/entry-pass-settings/discounts'],
          {
            state: {
              feedback: {
                text: successMessage,
                status: 'success'
              }
            }
          }
        );
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errors = reason.error;
          this.errorMessage = 'Please correct the errors shown and try again.';
        } else if (reason.status === 403) {
          if (this.isNewDiscount) {
            this.errorMessage = 'You do not have permission to add discounts.';
          } else {
            this.errorMessage = 'You do not have permission to modify this discount.';
          }
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSubmit = false; });
  }

  public handleDelete() {
    const modalRef = this.modalService.open(SimpleMessageModalComponent);
    modalRef.componentInstance.headerText = 'Delete Discount';
    modalRef.componentInstance.messageText = 'Are you sure you want to delete this discount? This action cannot be undone.';
    modalRef.componentInstance.messageAlertClass = 'alert-danger';
    modalRef.componentInstance.hasConfirmation = true;
    modalRef.componentInstance.confirmedCallback = () => {
      this.loadingDelete = true;

      this.entryPassService
        .deleteEntryPassDiscount(this.discount)
        .then(() => {
          let discountName = this.discount.name;
          if (!discountName) {
            discountName = this.discount.code;
          }

          this.router.navigate(
            ['/entry-pass-settings/discounts'],
            {
              state: {
                feedback: {
                  text: discountName + ' deleted.',
                  status: 'success'
                }
              }
            }
          )
        })
        .catch(reason => {
          if (reason.status === 400) {
            this.errors = reason.error;
            this.errorMessage = 'Please correct the errors shown and try again.';
          } else if (reason.status === 403) {
            this.errorMessage = 'You do not have permission to delete this discount.';
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => { this.loadingDelete = false; });
    }
  }

  public getFilteredEntryPasses(): EntryPass[] {
    return this.entryPasses.filter(ep => {
      if (this.initialEntryPassIds.includes(ep.id)) { return true; }

      return [EntryPassStatus.draft, EntryPassStatus.active].includes(ep.status);
    })
  }

  public entryPassSelected(entryPass: EntryPass) {
    return this.discount.entry_pass_ids.includes(entryPass.id);
  }

  public toggleEntryPass(entryPass: EntryPass) {
    if (this.entryPassSelected(entryPass)) {
      this.discount.entry_pass_ids.splice(this.discount.entry_pass_ids.indexOf(entryPass.id), 1);
    } else {
      this.discount.entry_pass_ids.push(entryPass.id);
    }
  }

  public allEntryPassesAreSelected() {
    for (const entryPass of this.getFilteredEntryPasses()) {
      if (!this.discount.entry_pass_ids.includes(entryPass.id)) {
        return false;
      }
    }

    return true;
  }

  public setNoEntryPassesSelected() {
    this.discount.entry_pass_ids = [];
  }

  public setAllEntryPassesSelected() {
    this.discount.entry_pass_ids = this.getFilteredEntryPasses().map(ep => ep.id);
  }

  public associationSelected(association: Association) {
    return this.discount.association_ids.includes(association.id);
  }

  public toggleAssociation(association: Association) {
    if (this.associationSelected(association)) {
      this.discount.association_ids.splice(this.discount.association_ids.indexOf(association.id), 1);
    } else {
      this.discount.association_ids.push(association.id);
    }
  }

  public allAssociationsAreSelected() {
    for (const association of this.associations) {
      if (!this.discount.association_ids.includes(association.id)) {
        return false;
      }
    }

    return true;
  }

  public setNoAssociationsSelected() {
    this.discount.association_ids = [];
  }

  public setAllAssociationsSelected() {
    this.discount.association_ids = this.associations.map(ep => ep.id);
  }

  public requiredMembershipSelected(membership: Membership) {
    return this.discount.required_membership_ids.includes(membership.id);
  }

  public toggleRequiredMembership(membership: Membership) {
    if (this.requiredMembershipSelected(membership)) {
      this.discount.required_membership_ids.splice(
        this.discount.required_membership_ids.indexOf(membership.id),
        1
      );
    } else {
      this.discount.required_membership_ids.push(membership.id);
    }
  }

  public allRequiredMembershipsAreSelected() {
    for (const membership of this.memberships) {
      if (!this.discount.required_membership_ids.includes(membership.id)) {
        return false;
      }
    }

    return true;
  }

  public setNoRequiredMembershipsSelected() {
    this.discount.required_membership_ids = [];
  }

  public setAllRequiredMembershipsSelected() {
    this.discount.required_membership_ids = this.memberships.map(m => m.id);
  }

  public isLoading() {
    return (
      this.loadingExistingDiscount ||
      this.loadingEntryPasses ||
      this.loadingAssociations ||
      this.loadingSubmit ||
      this.loadingDelete
    )
  }
}
